/**
 * Google Ads Conversion Tracking Service
 *
 * Singleton service for Google Ads conversion tracking via GA4 Measurement Protocol.
 * All conversions are sent server-side through /api/track/conversion, which then
 * sends to GA4. Google Ads receives conversions via GA4 import.
 *
 * This approach:
 * - Bypasses ad blockers (server-side tracking)
 * - Provides complete GA4 funnel data
 * - Uses environment prefixing (dev_ for non-production)
 */

import { hasCookieConsent } from '@lib/hooks/useCookieConsent'
import * as Sentry from '@sentry/nextjs'
import { isPrd } from '@shared/util/env'

import {
    CONVERSION_LABELS,
    type ConversionParams,
    getGA4EventName,
    type GoogleAdsConversionEvent,
} from './conversions'
import { type EnhancedUserData, prepareUserData } from './enhancedConversions'

function debugLog(...args: unknown[]) {
    if (!isPrd()) {
        console.info('[GoogleAdsService]', ...args)
    }
}

/**
 * Pending conversion to be sent when consent is granted
 */
interface PendingConversion {
    event: GoogleAdsConversionEvent
    params?: ConversionParams
    timestamp: number
}

/**
 * Hashed user data for enhanced conversions
 */
interface HashedUserData {
    sha256_email_address?: string[]
    sha256_phone_number?: string[]
    address?: {
        sha256_first_name?: string
        sha256_last_name?: string
    }
}

class GoogleAdsService {
    private initialized = false
    private pendingConversions: PendingConversion[] = []
    private pendingUserData: EnhancedUserData | null = null
    private hashedUserData: HashedUserData | null = null
    private clientId: string | null = null
    private gclid: string | null = null
    private readonly MAX_PENDING_CONVERSIONS = 100
    private readonly STORAGE_KEY = 'step_google_ads_pending'
    private readonly CLIENT_ID_KEY = 'step_ga4_client_id'
    private readonly GCLID_KEY = 'step_google_ads_gclid'
    // GA4 Measurement ID for reading session cookie (strip 'G-' prefix for cookie name)
    private readonly GA4_MEASUREMENT_ID =
        process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID || 'G-QCP7BKQ7WJ'

    /**
     * Initialize the Google Ads tracking service
     */
    initialize(): void {
        if (this.initialized || typeof window === 'undefined') {
            return
        }

        const conversionId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID
        const enabled = process.env.NEXT_PUBLIC_GOOGLE_ADS_ENABLED === 'true'

        if (!enabled || !conversionId) {
            debugLog('Disabled or missing conversion ID')
            return
        }

        this.loadPendingFromStorage()
        this.initializeClientId()
        this.loadGclidFromStorage()

        this.initialized = true
        debugLog('Initialized with ID:', conversionId)

        // Process any pending conversions if consent already granted
        if (hasCookieConsent()) {
            this.processPendingConversions()
        }
    }

    /**
     * Check if service is initialized and enabled
     */
    isInitialized(): boolean {
        return this.initialized
    }

    /**
     * Get the client ID for GA4 tracking.
     * Re-checks for GA4's _ga cookie each time to handle the case where
     * GA4 sets the cookie after our initial initialization.
     */
    getClientId(): string {
        // Always try to get the latest GA4 client ID from cookie
        // This handles the race condition where GA4 sets the cookie after we initialize
        const gaClientId = this.getGA4ClientIdFromCookie()
        if (gaClientId && gaClientId !== this.clientId) {
            this.clientId = gaClientId
            // Update localStorage with the GA4 client ID
            try {
                localStorage.setItem(this.CLIENT_ID_KEY, gaClientId)
            } catch {
                // Ignore storage errors
            }
            debugLog('Updated to GA4 _ga cookie client ID:', gaClientId)
        }

        return this.clientId || crypto.randomUUID()
    }

    /**
     * Set the Google Click ID (GCLID) for conversion attribution.
     * The GCLID is captured from URL parameters when users arrive from Google Ads.
     * It is persisted in sessionStorage and included in all conversion payloads.
     */
    setGclid(gclid: string): void {
        if (!gclid) return

        this.gclid = gclid
        this.saveGclidToStorage()
        debugLog('GCLID set:', gclid)
    }

    /**
     * Get the current GCLID
     */
    getGclid(): string | null {
        return this.gclid
    }

    /**
     * Get session ID from GA4's session cookie for proper session stitching.
     * Cookie name: _ga_<STREAM_ID> (e.g., _ga_QCP7BKQ7WJ for G-QCP7BKQ7WJ)
     * Cookie format: GS1.1.<session_id>.<session_count>.<engaged>.<timestamp>...
     */
    getSessionId(): string | null {
        if (typeof document === 'undefined') return null

        try {
            // Extract stream ID from measurement ID (remove 'G-' prefix)
            const streamId = this.GA4_MEASUREMENT_ID.replace('G-', '')
            const cookieName = `_ga_${streamId}`

            const sessionCookie = document.cookie
                .split('; ')
                .find((row) => row.startsWith(`${cookieName}=`))
                ?.split('=')[1]

            if (!sessionCookie) return null

            // Format: GS1.1.<session_id>.<session_count>.<engaged>.<timestamp>...
            const parts = sessionCookie.split('.')
            if (parts.length >= 3) {
                // Session ID is the third part
                return parts[2]
            }
        } catch {
            // Ignore cookie parsing errors
        }

        return null
    }

    /**
     * Track a conversion event
     */
    async trackConversion(
        event: GoogleAdsConversionEvent,
        params?: ConversionParams,
    ): Promise<void> {
        // Queue if not initialized yet - will be processed when initialize() is called
        if (!this.initialized) {
            this.queueConversion(event, params)
            debugLog('Not initialized, queued conversion:', event)
            return
        }

        // Check cookie consent
        if (!hasCookieConsent()) {
            this.queueConversion(event, params)
            return
        }

        await this.sendConversion(event, params)
    }

    /**
     * Set user data for enhanced conversions
     * Call this after user verification (KYC success) with email/phone
     */
    async setUserData(userData: EnhancedUserData): Promise<void> {
        if (!this.initialized) {
            // Store for later if not initialized
            this.pendingUserData = userData
            return
        }

        if (!hasCookieConsent()) {
            // Store for later if no consent
            this.pendingUserData = userData
            this.savePendingToStorage()
            return
        }

        await this.sendUserData(userData)
    }

    /**
     * Process pending conversions when consent is granted
     */
    async processPendingConversions(): Promise<void> {
        if (!this.initialized || !hasCookieConsent()) {
            return
        }

        // Send pending user data first for enhanced matching
        if (this.pendingUserData) {
            await this.sendUserData(this.pendingUserData)
            this.pendingUserData = null
        }

        // Send pending conversions
        const pending = [...this.pendingConversions]
        this.pendingConversions = []
        this.clearPendingFromStorage()

        for (const { event, params } of pending) {
            await this.sendConversion(event, params)
        }

        debugLog(`Processed ${pending.length} pending conversions`)
    }

    /**
     * Get or generate a client ID for GA4 user stitching.
     * Attempts to read from GA4's _ga cookie first for proper session stitching,
     * then falls back to localStorage, then generates a new UUID.
     */
    private initializeClientId(): void {
        if (typeof window === 'undefined') return

        try {
            // First, try to get client ID from GA4's _ga cookie for proper session stitching
            // Cookie format: GA1.1.<client_id>.<timestamp> or GA1.2.<client_id>.<timestamp>
            const gaClientId = this.getGA4ClientIdFromCookie()
            if (gaClientId) {
                this.clientId = gaClientId
                // Also store in localStorage as backup
                localStorage.setItem(this.CLIENT_ID_KEY, gaClientId)
                debugLog('Using GA4 _ga cookie client ID:', gaClientId)
                return
            }

            // Fallback: Try to get existing client ID from localStorage
            const stored = localStorage.getItem(this.CLIENT_ID_KEY)
            if (stored) {
                this.clientId = stored
                debugLog('Using stored client ID:', stored)
                return
            }

            // Last resort: Generate new UUID v4 client ID
            this.clientId = crypto.randomUUID()
            localStorage.setItem(this.CLIENT_ID_KEY, this.clientId)
            debugLog('Generated new client ID:', this.clientId)
        } catch {
            // Fallback if localStorage is not available
            this.clientId = crypto.randomUUID()
        }
    }

    /**
     * Extract client ID from GA4's _ga cookie.
     * Cookie format: GA1.1.<client_id>.<timestamp> or GA1.2.<client_id>.<timestamp>
     * The actual client_id for GA4 is "<client_id>.<timestamp>" (last two parts)
     */
    private getGA4ClientIdFromCookie(): string | null {
        if (typeof document === 'undefined') return null

        try {
            const gaCookie = document.cookie
                .split('; ')
                .find((row) => row.startsWith('_ga='))
                ?.split('=')[1]

            if (!gaCookie) return null

            // Format: GA1.1.123456789.1234567890 or GA1.2.123456789.1234567890
            const parts = gaCookie.split('.')
            if (parts.length >= 4) {
                // Client ID is the last two parts: <random_id>.<timestamp>
                return `${parts[2]}.${parts[3]}`
            }
        } catch {
            // Ignore cookie parsing errors
        }

        return null
    }

    /**
     * Send conversion event via server-side GA4 Measurement Protocol
     */
    private async sendConversion(
        event: GoogleAdsConversionEvent,
        params?: ConversionParams,
    ): Promise<void> {
        const config = CONVERSION_LABELS[event]
        if (!config) {
            Sentry.captureMessage(
                'Google Ads conversion label not configured',
                {
                    level: 'warning',
                    tags: {
                        errorType: 'missing_conversion_label',
                        analytics: 'google_ads',
                    },
                    extra: { event },
                },
            )
            return
        }

        // Get the GA4 event name with environment prefix
        const ga4EventName = getGA4EventName(event)

        // Build request body for server-side tracking
        const requestBody: {
            event: string
            clientId: string
            sessionId?: string
            transactionId?: string
            value?: number
            currency?: string
            userData?: HashedUserData
            gclid?: string
        } = {
            event: ga4EventName,
            clientId: this.getClientId(),
        }

        // Add session ID for GA4 session stitching (connects conversion to web session)
        const sessionId = this.getSessionId()
        if (sessionId) {
            requestBody.sessionId = sessionId
        }

        // Add GCLID for direct click attribution (improves conversion matching)
        if (this.gclid) {
            requestBody.gclid = this.gclid
        }

        // Add transaction ID for deduplication
        if (params?.transactionId) {
            requestBody.transactionId = params.transactionId
        }

        // Add value if provided and configured
        if (params?.value !== undefined && config.trackValue) {
            requestBody.value = params.value
            requestBody.currency = params.currency || config.currency || 'USD'
        }

        // Add hashed user data for enhanced conversions if available
        if (this.hashedUserData) {
            requestBody.userData = this.hashedUserData
        }

        try {
            const response = await fetch('/api/track/conversion', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            })

            if (!response.ok) {
                Sentry.captureMessage('Server-side conversion API failed', {
                    level: 'warning',
                    tags: {
                        errorType: 'server_conversion_failed',
                        analytics: 'google_ads',
                    },
                    extra: {
                        event,
                        ga4EventName,
                        status: response.status,
                        transactionId: params?.transactionId,
                    },
                })
            }

            debugLog('Sent server-side conversion:', ga4EventName, requestBody)
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'server_conversion_error',
                    analytics: 'google_ads',
                },
                extra: {
                    event,
                    ga4EventName,
                    transactionId: params?.transactionId,
                },
            })
        }
    }

    /**
     * Prepare and store user data for enhanced conversions.
     * The hashed data will be included in subsequent conversion API calls.
     */
    private async sendUserData(userData: EnhancedUserData): Promise<void> {
        try {
            const hashedData = await prepareUserData(userData)

            // Only store if we have some data
            if (
                hashedData.sha256_email_address ||
                hashedData.sha256_phone_number
            ) {
                this.hashedUserData = hashedData as HashedUserData
                debugLog('Stored enhanced conversion user data for API calls')
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'user_data_preparation_failed',
                    analytics: 'google_ads',
                },
                extra: {
                    hasEmail: !!userData.email,
                    hasPhone: !!userData.phone,
                },
            })
        }
    }

    /**
     * Queue conversion for later when consent is granted
     */
    private queueConversion(
        event: GoogleAdsConversionEvent,
        params?: ConversionParams,
    ): void {
        // Prevent memory leaks
        if (this.pendingConversions.length >= this.MAX_PENDING_CONVERSIONS) {
            this.pendingConversions.shift() // Remove oldest
        }

        this.pendingConversions.push({
            event,
            params,
            timestamp: Date.now(),
        })

        this.savePendingToStorage()

        debugLog('Queued conversion (no consent):', event)
    }

    /**
     * Save pending data to sessionStorage
     */
    private savePendingToStorage(): void {
        if (typeof window === 'undefined') return

        try {
            const data = {
                conversions: this.pendingConversions,
                userData: this.pendingUserData,
            }
            sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data))
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Load pending data from sessionStorage
     */
    private loadPendingFromStorage(): void {
        if (typeof window === 'undefined') return

        try {
            const stored = sessionStorage.getItem(this.STORAGE_KEY)
            if (stored) {
                const data = JSON.parse(stored)
                this.pendingConversions = data.conversions || []
                this.pendingUserData = data.userData || null
            }
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Clear pending data from sessionStorage
     */
    private clearPendingFromStorage(): void {
        if (typeof window === 'undefined') return

        try {
            sessionStorage.removeItem(this.STORAGE_KEY)
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Save GCLID to sessionStorage for persistence across page navigations
     */
    private saveGclidToStorage(): void {
        if (typeof window === 'undefined' || !this.gclid) return

        try {
            sessionStorage.setItem(this.GCLID_KEY, this.gclid)
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Load GCLID from sessionStorage
     */
    private loadGclidFromStorage(): void {
        if (typeof window === 'undefined') return

        try {
            const stored = sessionStorage.getItem(this.GCLID_KEY)
            if (stored) {
                this.gclid = stored
                debugLog('Loaded GCLID from storage:', stored)
            }
        } catch {
            // Ignore storage errors
        }
    }
}

// Export singleton instance
const googleAdsService = new GoogleAdsService()
export default googleAdsService
